import { atom } from 'nanostores';
import type { HotEditSelection } from '../stores/hotEdit';
import { applyClassDelta } from './tailwindPatch';

/**
 * The founder's changes before Apply (Prototype Editor UX plan, step 2).
 *
 * Every tweak used to write a file the moment it was made, through six debounce queues. On the
 * light preview each write reloads the page, so a founder trying three sizes rebuilt the
 * prototype three times, and there was no way back except undo. Now a change shows at once in the
 * preview as an inline style or text - nothing is written - and Apply writes them all in one
 * pass, followed by a single recompile.
 *
 * Pure apart from the store: the collapsing rules are what keep "Apply 3" honest.
 */
export type PendingEdit =
  | {
      id: string;
      kind: 'class';
      elementId: string;
      selection: HotEditSelection;
      classDelta: string;
      /** Classes to take out of the element's className, e.g. `hidden` when unhiding it. */
      classRemove?: string;
      /** Leave conflicting classes in place, so hiding does not cost the element its display. */
      keepConflicting?: boolean;
      /** This edit is the whole story for the element: it replaces rather than merges. */
      classReplace?: boolean;
      label: string;
    }
  | { id: string; kind: 'text'; elementId: string; selection: HotEditSelection; text: string; label: string }
  | {
      id: string;
      kind: 'attr';
      elementId: string;
      selection: HotEditSelection;
      attribute: string;
      value: string;
      label: string;
    }
  | { id: string; kind: 'options'; elementId: string; selection: HotEditSelection; options: string[]; label: string }
  | {
      id: string;
      kind: 'theme';
      /** The token is the target, not the element: two buttons set to the same Primary are one change. */
      elementId: string;
      selection: HotEditSelection;
      token: string;
      value: string;
      label: string;
    }
  | {
      id: string;
      kind: 'data';
      /** The list's data file, which is the target: `elementId` is only for the count. */
      elementId: string;
      /**
       * The file is the target, so this is only what the founder had picked at the time - and
       * after deleting the row they were on, that is nothing. It used to be required, and the
       * change was dropped without a word while the preview went on showing it.
       */
      selection: HotEditSelection | null;
      filePath: string;
      /** The file as it will be written - each edit is computed from the one before it. */
      content: string;
      label: string;
    }
  | {
      id: string;
      kind: 'icon';
      elementId: string;
      selection: HotEditSelection;
      /** The Lucide component to draw instead, e.g. `Package`. */
      iconName: string;
      label: string;
    }
  | {
      id: string;
      kind: 'image';
      elementId: string;
      selection: HotEditSelection;
      /** The asset the picture is coming from, when the preview was showing a stored one. */
      fromStorePath: string | null;
      /** `public/media/<file>` and `/media/<file>`: the new picture, as stored and as referenced. */
      storePath: string;
      reference: string;
      dataUri: string;
      label: string;
    };

/** A change on its way into the list: the same shapes, before they are given an id. */
export type PendingEditInput =
  | Omit<Extract<PendingEdit, { kind: 'class' }>, 'id'>
  | Omit<Extract<PendingEdit, { kind: 'text' }>, 'id'>
  | Omit<Extract<PendingEdit, { kind: 'attr' }>, 'id'>
  | Omit<Extract<PendingEdit, { kind: 'options' }>, 'id'>
  | Omit<Extract<PendingEdit, { kind: 'theme' }>, 'id'>
  | Omit<Extract<PendingEdit, { kind: 'image' }>, 'id'>
  | Omit<Extract<PendingEdit, { kind: 'icon' }>, 'id'>
  | Omit<Extract<PendingEdit, { kind: 'data' }>, 'id'>;

export const pendingEdits = atom<PendingEdit[]>([]);

let counter = 0;

function nextId(): string {
  counter += 1;
  return `e${counter}`;
}

/** Two edits are the same change when they touch the same thing on the same element. */
function sameTarget(a: PendingEdit, b: PendingEditInput): boolean {
  if (a.kind !== b.kind) return false;
  // Every change to one data file collapses into the latest copy of that file: the grid
  // computes each edit from the pending content, so the last one already holds the rest.
  if (a.kind === 'data') return a.filePath === (b as Extract<PendingEditInput, { kind: 'data' }>).filePath;
  // A design system colour belongs to the prototype, so picking Primary twice is one change
  // whichever element it was picked on.
  if (a.kind === 'theme') return a.token === (b as Extract<PendingEditInput, { kind: 'theme' }>).token;
  if (a.elementId !== b.elementId) return false;
  if (a.kind === 'attr') return a.attribute === (b as Extract<PendingEditInput, { kind: 'attr' }>).attribute;
  return true;
}

/**
 * The list with `edit` added. A second change to the same thing replaces the first, so three
 * tries at a size are one pending change; class changes merge, so the element keeps one delta.
 */
export function queueEdit(list: readonly PendingEdit[], edit: PendingEditInput): PendingEdit[] {
  const at = list.findIndex((e) => sameTarget(e, edit));
  if (at < 0) return [...list, { ...edit, id: nextId() } as PendingEdit];

  const existing = list[at]!;
  const merged: PendingEdit =
    existing.kind === 'class' && edit.kind === 'class' && !edit.classReplace
      ? {
          ...existing,
          classDelta: applyClassDelta(existing.classDelta, edit.classDelta),
          classRemove: edit.classRemove ?? existing.classRemove,
          keepConflicting: existing.keepConflicting || edit.keepConflicting,
          label: edit.label,
        }
      : { ...edit, id: existing.id };
  const out = [...list];
  out[at] = merged;
  return out;
}

/** The list with the last change dropped, and the change itself so the preview can be put back. */
export function dropLast(list: readonly PendingEdit[]): { list: PendingEdit[]; dropped: PendingEdit | null } {
  if (list.length === 0) return { list: [...list], dropped: null };
  return { list: list.slice(0, -1), dropped: list[list.length - 1]! };
}

/** What Apply is about to write, named the way the founder made them: for the leave prompt. */
export function describeEdits(list: readonly PendingEdit[]): string {
  const labels = list.map((e) => e.label);
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0]!;
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/** Every element with a pending change, so the preview can be put back element by element. */
export function touchedElements(list: readonly PendingEdit[]): string[] {
  return [...new Set(list.map((e) => e.elementId))];
}

/** A change the preview is told to make to one element. */
export type DiscardPatch = { kind: 'text'; value: string } | { kind: 'dom'; key: 'src'; value: string };

/**
 * What Discard puts back on an element once its inline changes are cleared: only what a pending
 * edit actually replaced. Text was written back whatever the edit was, and the bridge sets it as
 * `textContent`, so discarding a colour change on a header box replaced its children with one
 * line of plain text until the page reloaded (10 Oct 2026).
 *
 * `selection` is the element picked right now, the only one this has a text and picture for.
 * Known limits, as before this change: a text or picture edit on an element that is no longer
 * the one picked stays in the preview until it reloads, and so do attribute, option and icon
 * edits. Nothing is saved in any of these cases.
 */
export function discardRestorePatches(
  elementId: string,
  edits: readonly PendingEdit[],
  selection: HotEditSelection | null | undefined,
): DiscardPatch[] {
  if (!selection || selection.elementId !== elementId) return [];
  const mine = edits.filter((e) => e.elementId === elementId);
  const patches: DiscardPatch[] = [];
  if (mine.some((e) => e.kind === 'text') && !selection.hasChildElements) {
    patches.push({ kind: 'text', value: selection.textContent });
  }
  if (mine.some((e) => e.kind === 'image') && selection.imageSrc) {
    patches.push({ kind: 'dom', key: 'src', value: selection.imageSrc });
  }
  return patches;
}

export function clearPendingEdits(): void {
  pendingEdits.set([]);
}
